import { useContext, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';

import {
  Box,
  Button,
  Chip,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import { ConfirmDialog } from '../../../components/dialogs';
import { SearchField } from '../../../components/inputs';
import { Pagination } from '../../../components/paginations';
import { AlertSnackbarContext } from '../../../components/snackbars';
import { Spinner } from '../../../components/spinners';
import { Defaults } from '../../../constants';
import { usePinCategory, useUnpinCategory } from '../../../lib/category/hooks';
import { useDeleteSubCategory } from '../../../lib/category/hooks/useDeleteSubCategory';
import { useGetSubCategoriesByPage } from '../../../lib/category/hooks/useGetSubCategoriesByPage';
import { useUpdateSubCategory } from '../../../lib/sub-category/hooks';
import { SortDirection } from '../../../lib/util/helpers';

const SubCategoryList = () => {
  const navigate = useNavigate();
  const [, setAlertSnackbar] = useContext(AlertSnackbarContext);
  const handleEditSubCategory = (subCategoryId: number) => navigate(`/cms/sub-categories/${subCategoryId}`);
  const { mutateAsync: deleteSubCategory, isLoading: isLoadingDelete } = useDeleteSubCategory();

  const [optionsUpdated, setOptionsUpdated] = useState(true);
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    search: StringParam,
    sort: StringParam,
  });
  const { page, search, sort } = query;
  const searchString = search && { search };
  const debouncedSearchFn = useDebouncedCallback((value: string) => {
    setQuery({ search: value, page: 0 });
  }, Defaults.SEARCH_DEBOUNCE_MS);

  const {
    data: subCategories,
    isLoading,
    error,
  } = useGetSubCategoriesByPage({
    page: Number(page) || 0,
    sort: String(sort || ''),
    ...searchString,
  });

  const { mutateAsync: updateSubCategory, isLoading: isLoadingUpdate } = useUpdateSubCategory();

  const handlePromote = async (subCategoryId: string) => {
    await updateSubCategory(
      {
        subCategoryId,
        params: {
          category: null,
          published: true,
        },
      },
      {
        onSuccess: () => {
          setAlertSnackbar({
            message: 'Sub Category promoted',
            severity: 'success',
            vertical: 'top',
          });
        },
      },
    );
  };

  useEffect(() => {
    if (!optionsUpdated) {
      return;
    }
    setOptionsUpdated(false);
  }, [optionsUpdated, page, sort, search]);

  useEffect(() => {
    setOptionsUpdated(true);
  }, [setOptionsUpdated, page, sort, search]);

  const setNewSort = (column: string, direction: SortDirection) => {
    const newSort = direction ? `${column} ${direction}` : undefined;
    setQuery({ sort: newSort });
  };

  const setNewPage = (page: number) => setQuery({ page: page - 1 });

  const sortToObj = (sortStr: string | undefined) => {
    const initialValues = { column: '', direction: undefined };
    if (typeof sortStr !== 'string') {
      return initialValues;
    }
    const [column, direction] = sortStr.split(' ');
    if (typeof column !== 'string' || !['asc', 'desc', undefined].includes(direction)) {
      return initialValues;
    }
    return { ...initialValues, column, direction: direction as SortDirection };
  };

  const sortObj = sortToObj(sort ?? '');

  const columns = [
    {
      text: 'Name',
      name: 'category.title',
    },
    {
      text: 'Category',
      name: 'parent.title',
    },
    {
      text: 'Pinned',
      name: 'category.pinned',
    },
    {
      text: 'Actions',
      name: '',
    },
  ];

  const { mutateAsync: pinCategory, isLoading: isLoadingPin } = usePinCategory();
  const { mutateAsync: unpinCategory, isLoading: isLoadingUnpin } = useUnpinCategory();

  return (
    <Container>
      <FullWidthBar>
        <Typography variant="h5" sx={{ marginRight: '20px' }}>
          Sub Categories
        </Typography>
        <StyledSearchField
          searchString={search ?? ''}
          onSearch={(search: string) => {
            debouncedSearchFn(search);
          }}
        />
      </FullWidthBar>

      <TableContainer sx={{ backgroundColor: 'white' }}>
        <Table aria-labelledby="tableTitle">
          <TableHead>
            <PointerTableRow>
              {columns.map((column) => (
                <TableCell key={column.name}>
                  <TableSortLabel
                    active={sortObj.column === column.name}
                    direction={sortObj.direction}
                    onClick={() => {
                      const isAsc = sortObj.column === column.name && sortObj.direction === 'asc';
                      setNewSort(column.name, isAsc ? 'desc' : 'asc');
                    }}
                    component="a"
                  >
                    {column.text}
                    {sortObj.column === column.name ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sortObj.direction === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </PointerTableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Spinner />
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              !error &&
              subCategories?.data &&
              subCategories?.data.map((row: any, index: number) => {
                return (
                  <StyledTableRow key={index} className={row.deleted ? 'deleted-row' : 'row'}>
                    <TableCell>{row.title}</TableCell>
                    <TableCell>{row.parent.title}</TableCell>
                    <TableCell>
                      {row.pinned ? (
                        <Chip variant="outlined" label="Pinned" color="primary" />
                      ) : (
                        <Chip variant="outlined" label="Not Pinned" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Button onClick={() => handleEditSubCategory(row.id)} type="button" style={{ textTransform: 'capitalize' }}>
                        Edit
                      </Button>

                      <ConfirmDialog
                        trigger={(open) => (
                          <Button onClick={open} type="button" style={{ textTransform: 'none' }}>
                            Convert to Category
                          </Button>
                        )}
                        title="Convert to Category"
                        description="Are you sure you want to convert this sub-category to category?"
                        cancelBtnText={'Cancel'}
                        submitBtnText={'Submit'}
                        onConfirm={async (close) => {
                          await handlePromote(row.id);
                          close();
                        }}
                        disabled={isLoadingUpdate}
                        loading={isLoadingUpdate}
                      />

                      <ConfirmDialog
                        trigger={(open) => (
                          <Button onClick={open} type="button" style={{ textTransform: 'capitalize' }}>
                            Delete
                          </Button>
                        )}
                        title="Delete Sub-Category"
                        description="Are you sure you want to delete this sub-category?"
                        cancelBtnText={'Cancel'}
                        submitBtnText={'Submit'}
                        onConfirm={async (close) => {
                          await deleteSubCategory(row.id);
                          close();
                        }}
                        disabled={isLoadingDelete}
                        loading={isLoadingDelete}
                      />

                      {!row.pinned ? (
                        <ConfirmDialog
                          trigger={(open) => (
                            <Button onClick={open} type="button" style={{ textTransform: 'capitalize' }}>
                              Pin
                            </Button>
                          )}
                          title="Pin Sub-Category"
                          description="Are you sure you want to pin this sub-category?"
                          cancelBtnText={'Cancel'}
                          submitBtnText={'Submit'}
                          onConfirm={async (close) => {
                            await pinCategory(row.id, {
                              onSuccess: () => {
                                setAlertSnackbar({ message: 'Sub-Category pinned successfully.', severity: 'success' });
                              },
                              onError: (error: any) => {
                                setAlertSnackbar({ message: error.message, severity: 'error' });
                              },
                            });
                            close();
                          }}
                          disabled={isLoadingPin}
                          loading={isLoadingPin}
                        />
                      ) : (
                        <ConfirmDialog
                          trigger={(open) => (
                            <Button onClick={open} type="button" style={{ textTransform: 'capitalize' }}>
                              Unpin
                            </Button>
                          )}
                          title="Unpin Sub-Category"
                          description="Are you sure you want to unpin this sub-category?"
                          cancelBtnText={'Cancel'}
                          submitBtnText={'Submit'}
                          onConfirm={async (close) => {
                            await unpinCategory(row.id, {
                              onSuccess: () => {
                                setAlertSnackbar({ message: 'Sub-Category unpinned successfully.', severity: 'success' });
                              },
                              onError: (error: any) => {
                                setAlertSnackbar({ message: error.message, severity: 'error' });
                              },
                            });
                            close();
                          }}
                          disabled={isLoadingUnpin}
                          loading={isLoadingUnpin}
                        />
                      )}
                    </TableCell>
                  </StyledTableRow>
                );
              })}
            {subCategories?.data?.length === 0 && (
              <StyledTableRow>
                <TableCell colSpan={5} style={{ textAlign: 'center' }}>
                  No sub categories records found.
                </TableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <FullWidthBar>
        <Typography variant="subtitle1">Total: {subCategories?.total}</Typography>
        {!!subCategories?.total && !!subCategories.limit && (
          <StyledPagination
            current={Number(page) + 1 || 1}
            total={Math.ceil(subCategories.total / subCategories.limit)}
            onPageChange={(page: number) => setNewPage(page)}
          />
        )}
      </FullWidthBar>
    </Container>
  );
};

export default SubCategoryList;

const Container = styled('div')({
  padding: '20px',
  backgroundColor: '#eee',
  flex: 1,
  display: 'flex',
  alignItems: 'stretch',
  flexDirection: 'column',
});

const FullWidthBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const PointerTableRow = styled(TableRow)({
  cursor: 'pointer',
});

const StyledTableRow = styled(PointerTableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StyledPagination = styled(Pagination)({
  alignSelf: 'flex-end',
});

const StyledSearchField = styled(SearchField)({
  marginLeft: 'auto',
});
